import { SetupArea, TeamSetupArea, useSetupAreasService } from 'nekst-api';
import React, { ReactNode, useMemo, useState } from 'react';
import {
  Header4,
  Header5,
  InputLayoutIcon,
  LoadingAnimation,
  SimpleBlock,
  TextInput,
  useFormContextRequired,
  useListContextRequired,
  useNewUiTheme
} from 'ui-builder';
import styles from './SetupAreaChooser.module.scss';
import { InformationMessage, Separator } from 'features/nekst-widgets';
import { useDataLoaderNew } from 'data-loader';
import { ClickableBlock } from '../ClickableBlock';
import { useStatesHelper } from '../states';
import { Spacings } from 'react-native-ui-lib';
import { ViewStyle } from 'react-native';

type StateGroup = {
  state: string;
  areas: SetupArea[],
};

function InnerBlock(props: {
  children: React.ReactNode;
  style?: ViewStyle,
}) {
  return (
    <SimpleBlock className={styles.innerBlock} style={props.style}>
      {props.children}
    </SimpleBlock>
  );
}

export function SetupAreaChooserWidget(
  props: {
    onChange: (setupArea: SetupArea) => void,
    value?: SetupArea,
    setupAreas: SetupArea[],
    requestNewMarketWidget?: ReactNode,
  }
) {

  const uiTheme = useNewUiTheme();

  const [nameContains, setNameContains] = useState<string>();

  const statesHelper = useStatesHelper();

  const setupAreaId = props.value?.id;

  const setupAreas = useMemo(() => {
    return props.setupAreas.filter((area) => {
      const stateName = statesHelper.getLabelByValue(area.market?.state || '');

      if (nameContains) {
        return area.name.toLowerCase()
            .includes(nameContains.toLowerCase()) ||
          (stateName && stateName.toLowerCase()
            .includes(nameContains.toLowerCase()));
      } else {
        return true;
      }
    });
  }, [props.setupAreas.length, nameContains]);

  const statesRecord: Record<string, SetupArea[]> = {};
  setupAreas
    .forEach((area) => {
      const state = area.market?.state || '';

      if (!statesRecord[state]) {
        statesRecord[state] = [];
      }
      statesRecord[state].push(area);
    });

  const countriesRecord: Record<string, StateGroup[]> = {};
  Object.keys(statesRecord)
    .forEach((state) => {
      const country = statesHelper.getCountryByValue(state);
      if (!countriesRecord[country]) {
        countriesRecord[country] = [];
      }
      countriesRecord[country].push({
        state,
        areas: statesRecord[state]
      });
    });

  const hasOptions = Object.keys(countriesRecord).length > 0 || nameContains;

  return (
    <>
      {!hasOptions ? (<InformationMessage withoutMargin>
        No markets available.
      </InformationMessage>) : null}

      {hasOptions ? (
        <>
          <TextInput
            source="nameContains"
            placeholder="Type state/province or market name to search"
            hideLabel
            value={nameContains}
            onChangeCallback={(event) => {
              setNameContains(event.nameContains);
            }}
            icon={InputLayoutIcon.SEARCH}
          />
          <Separator />
        </>
      ) : null}
      {props.requestNewMarketWidget}
      {Object.keys(countriesRecord).length === 0 && nameContains && (
        <InformationMessage withoutMargin>
          No markets found for "{nameContains}".
        </InformationMessage>
      )}
      <uiTheme.forms.BaseInputLayout name="setupAreaId" showRequiredIcon={false} hideLabel>
        {Object.entries(countriesRecord)
          .map(([countryName, stateGroups]) => {
            return (
              <SimpleBlock
                key={`country-${countryName}`}
                style={{
                  marginBottom: Spacings.s2
                }}
              >
                {countryName && (
                  <Header4 style={{ marginBottom: Spacings.s2 }}>{countryName}</Header4>)}
                <SimpleBlock>
                  {stateGroups
                    .sort((a, b) => {
                      const labelA = statesHelper.getLabelByValue(a.state);
                      const labelB = statesHelper.getLabelByValue(b.state);
                      return (labelA || '').localeCompare(labelB || '');
                    })
                    .map((stateGroup, index: number) => {
                    const stateLabel = statesHelper.getLabelByValue(stateGroup.state);

                    return (
                      <InnerBlock style={{
                        marginBottom: (index !== stateGroups.length - 1) ? Spacings.s2 : 0
                      }}>
                        {stateLabel && (
                          <Header5 style={{ marginBottom: Spacings.s1 }}>{stateLabel}</Header5>)}

                        {stateGroup.areas.map((area) => {
                          const title = area.isDefault ? `${area.name} (No Market Chosen)` : area.name;


                          return (
                            <ClickableBlock
                              title={title}
                              onClick={() => props.onChange(area)}
                              key={`area-${area.id}`}
                              small
                              active={setupAreaId === area.id}
                            />
                          );
                        })}
                      </InnerBlock>
                    );
                  })}
                </SimpleBlock>
              </SimpleBlock>
            );
          })}

      </uiTheme.forms.BaseInputLayout>
    </>

  );
}

export function SetupAreaChooser(
  props: {
    requestNewMarketWidget?: ReactNode,
  }
) {

  const formContext = useFormContextRequired();

  const setupAreasService = useSetupAreasService();

  const onChange = (setupArea: SetupArea) => {
    formContext.onChangeCallback({
      setupAreaId: setupArea.id,
    });
  };

  const { data: setupAreas } = useDataLoaderNew(setupAreasService.getAllSetupAreas);

  const { data: teamSetupAreas } = useListContextRequired<TeamSetupArea>();

  const hasAlreadyAssigned = (id: number) => teamSetupAreas?.find((item) => item.id === id);

  const setupAreaId = formContext.data?.setupAreaId;

  if (setupAreas && teamSetupAreas) {
    return (
      <SetupAreaChooserWidget
        onChange={onChange}
        value={setupAreas.find((item) => item.id === setupAreaId)}
        setupAreas={setupAreas
          .filter((area) => area.name !== 'Internal')
          .filter((area) => !hasAlreadyAssigned(area.id))}
        requestNewMarketWidget={props.requestNewMarketWidget}
      />
    );
  } else {
    return (
      <LoadingAnimation />
    );
  }
}
